import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { fetchMerchantDrilldown, MerchantSummary } from '@/services/api/transactionApi';
import { Transaction } from '@/types/Transaction';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const MerchantSummaryPage = () => {
  const { merchantName } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [summary, setSummary] = useState<MerchantSummary | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  const timeframe = searchParams.get('timeframe') ?? 'month';
  const date = searchParams.get('date') ?? '2025-06-01';

  useEffect(() => {
    const load = async () => {
      const result = await fetchMerchantDrilldown(merchantName!, timeframe, date);
      setSummary(result.summary);
      setTransactions(result.transactions);
    };
    load();
  }, [merchantName, timeframe, date]);

  const changeTimeframe = (newTimeframe: string) => {
    setSearchParams({ timeframe: newTimeframe, date });
  };

  const shiftYear = (offset: number) => {
    const newDate = new Date(date);
    newDate.setFullYear(newDate.getFullYear() + offset);
    const formatted = newDate.toISOString().split('T')[0];
    setSearchParams({ timeframe, date: formatted });
  };

  if (!summary) return <div className="p-4">Loading...</div>;

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header Row */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-sm text-gray-500 flex items-center gap-1">
          <span
            className="text-blue-600 hover:underline cursor-pointer flex items-center gap-1"
            onClick={() => navigate('/dashboard')}
          >
            ← Dashboard
          </span>{' '}
          &gt; <span className="text-purple-600 font-medium">{merchantName}</span>
        </h1>

        <div className="flex flex-wrap gap-2 items-center">
          {['month', 'quarter', 'year'].map((tf) => (
            <button
              key={tf}
              onClick={() => changeTimeframe(tf)}
              className={`px-4 py-2 rounded text-sm font-medium border ${
                timeframe === tf ? 'bg-black text-white' : 'bg-white text-black border-gray-300'
              }`}
            >
              {tf[0].toUpperCase() + tf.slice(1)}
            </button>
          ))}
          <button className="px-4 py-2 border rounded text-sm hover:bg-gray-100">Edit merchant</button>
          <button className="px-4 py-2 border rounded text-sm hover:bg-gray-100">Filters</button>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex justify-between mb-2">
          <h2 className="text-lg font-semibold">Monthly Breakdown</h2>
          <div className="text-sm text-gray-500 flex gap-2 items-center">
            <button onClick={() => shiftYear(-1)}>&larr;</button>
            <span>{new Date(date).getFullYear()}</span>
            <button onClick={() => shiftYear(1)}>&rarr;</button>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={summary.monthlyBreakdown}>
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="total" fill="#4ade80" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-500">Total Transactions</p>
          <p className="text-xl font-bold">{summary.totalTransactions}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-500">Total Amount</p>
          <p className="text-xl font-bold text-green-600">{summary.totalAmount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-500">Average Transaction</p>
          <p className="text-xl font-bold text-green-600">{summary.averageAmount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-500">Largest Transaction</p>
          <p className="text-xl font-bold text-green-600">
            {Math.max(...transactions.map((t) => Math.abs(t.amount))).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Transactions Table View */}
      <div className="bg-white rounded-xl shadow p-4">
        <h2 className="text-lg font-semibold mb-4">Transactions</h2>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left border">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="py-2 px-4 border-b">Date</th>
                <th className="py-2 px-4 border-b">Merchant</th>
                <th className="py-2 px-4 border-b">Category</th>
                <th className="py-2 px-4 border-b">Account</th>
                <th className="py-2 px-4 border-b text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="py-2 px-4 border-b">
                    {new Date(t.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </td>
                  <td className="py-2 px-4 border-b">{t.merchantName}</td>
                  <td className="py-2 px-4 border-b">{t.category || 'Uncategorized'}</td>
                  <td className="py-2 px-4 border-b">{t.accountName || 'Unknown Account'}</td>
                  <td
                    className={`py-2 px-4 border-b text-right font-semibold ${
                      t.amount >= 0 ? 'text-green-600' : 'text-gray-700'
                    }`}
                  >
                    {t.amount >= 0 ? `+$${t.amount.toFixed(2)}` : `$${t.amount.toFixed(2)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MerchantSummaryPage;
